// Таблицата на „Разнесени отчети": ниски редове на един ред. Широк екран —
// Документ · Отчет · Дата и час · Потребител · Продадени · Изписани · Замени ·
// Нето · Бруто · Статус; лаптоп/таблет — Документ · Отчет · Дата и час ·
// Потребител · Бруто · Статус; телефон — карти. Филтрите са в заглавията
// „Потребител" (Разнесъл) и „Статус". Анулираните стоят зачертани и сиви, а
// редът „Общо за периода" (етикетът — точно пред сумите) е без тях; „· без
// анулирани" пише само когато в резултата ги има. Клавиатура: ↑/↓ по редовете, Enter
// отваря документа.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ReportPosting } from "../../../../api/inventory";
import { fmtDateTime } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import { StatusPill } from "../../components/list/ListBits";
import { RowActionsMenu, type RowAction } from "../../components/list/RowActionsMenu";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { isPostingAnnulled } from "../../posting/components/reportPostingShared";
import { ReportHead } from "../components/ReportListBits";
import {
  REPORT_TOTAL_LABEL_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import type { PostingFilterKey } from "./postedReportsModel";

export type TableDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS: Record<Exclude<TableDensity, "cards">, string> = {
  full: "grid grid-cols-[64px_88px_128px_minmax(90px,.8fr)_minmax(140px,1.1fr)_68px_92px_92px_96px_88px_92px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[64px_minmax(80px,1fr)_128px_minmax(90px,1fr)_96px_88px_92px] items-center gap-2.5 px-[18px]",
};

type Props = {
  rows: ReportPosting[];
  density: TableDensity;
  flashId: number | null;
  filters: ColumnSections<PostingFilterKey>;
  totalNet: number;
  totalGross: number;
  // В целия филтриран резултат има анулирани (сумите са без тях)
  totalExcludesAnnulled: boolean;
  onOpen: (posting: ReportPosting) => void;
  actionsOf: (posting: ReportPosting) => RowAction[];
  onPrint: (posting: ReportPosting) => void;
  footer?: ReactNode;
};

export const PostedReportsTable = ({
  rows,
  density,
  flashId,
  filters,
  totalNet,
  totalGross,
  totalExcludesAnnulled,
  onOpen,
  actionsOf,
  onPrint,
  footer,
}: Props) => {
  const { t } = useTranslation();

  const statusPill = (posting: ReportPosting) =>
    isPostingAnnulled(posting) ? (
      <StatusPill tone="neutral">{t("inventoryPage.statusAnnulled")}</StatusPill>
    ) : (
      <StatusPill tone="ok">{t("inventoryPage.statusPosted")}</StatusPill>
    );
  const flashClass = (posting: ReportPosting) =>
    posting.id === flashId ? "bg-brand-tint" : "bg-transparent";
  const numberClass = (posting: ReportPosting) =>
    isPostingAnnulled(posting) ? "text-faint line-through" : "text-ink";
  const reportTitle = (posting: ReportPosting) =>
    posting.reportNumber != null
      ? t("inventoryPage.postingReportTitle", { number: posting.reportNumber })
      : "—";

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((posting) => (
          <div
            key={posting.id}
            {...openableRowProps(() => onOpen(posting))}
            className={`flex cursor-pointer flex-col gap-1.5 border-t border-shade-1 px-3.5 py-3 outline-none transition-colors duration-500 focus-visible:bg-panel ${flashClass(posting)}`}
          >
            <div className="flex items-center gap-2">
              <span className="font-sheet font-semibold text-ink">№{posting.id}</span>
              <span className="text-ink-2">{reportTitle(posting)}</span>
              <span className="flex-1" />
              {statusPill(posting)}
              <RowActionsMenu actions={actionsOf(posting)} compact />
            </div>
            <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
              <span>
                {fmtDateTime(posting.createdAt ?? posting.date)} · {posting.createdByName ?? "—"}
              </span>
              <span className={`font-sheet text-sm font-semibold ${numberClass(posting)}`}>
                {formatMoney(posting.totalValueWithVat || 0)}
              </span>
            </div>
          </div>
        ))}
        {footer}
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1184px]" : "min-w-[724px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          <ReportHead label={t("inventoryPage.colDocument")} />
          <ReportHead label={t("inventoryPage.colReport")} />
          <ReportHead label={t("inventoryPage.colDateTime")} />
          <ReportHead label={t("inventoryPage.colUser")} filters={sectionList(filters.users)} />
          {full && (
            <>
              <ReportHead label={t("inventoryPage.postingColSold")} />
              <ReportHead label={t("inventoryPage.colWrittenOff")} align="right" />
              <ReportHead label={t("inventoryPage.colSubstitutions")} />
              <ReportHead label={t("inventoryPage.colNet")} align="right" />
            </>
          )}
          <ReportHead label={t("inventoryPage.colGross")} align="right" />
          <ReportHead label={t("inventoryPage.colStatus")} filters={sectionList(filters.statuses)} />
          <span />
        </div>
        {rows.map((posting) => (
          <div
            key={posting.id}
            {...openableRowProps(() => onOpen(posting))}
            className={`${grid} ${REPORT_OPENABLE_ROW_CLASS} transition-colors duration-500 ${flashClass(posting)}`}
          >
            <span className="font-sheet font-semibold text-ink">№{posting.id}</span>
            <span className="truncate text-ink">{reportTitle(posting)}</span>
            <span className="whitespace-nowrap font-sheet text-ink-2">{fmtDateTime(posting.createdAt ?? posting.date)}</span>
            <span className="truncate font-medium text-ink">{posting.createdByName ?? "—"}</span>
            {full && (
              <>
                <span className="truncate text-ink-2">
                  {posting.soldRecipesCount} {t("inventoryPage.postingSoldRecipes")} ·{" "}
                  {posting.soldProductsCount} {t("inventoryPage.postingSoldProducts")}
                </span>
                <span className="text-right font-sheet text-ink-2">{posting.productsCount}</span>
                <span className="flex min-w-0">
                  {posting.substitutionsCount > 0 ? (
                    <StatusPill tone="brand">
                      {t("inventoryPage.postingSubsBadge", { count: posting.substitutionsCount })}
                    </StatusPill>
                  ) : (
                    <span className="text-faint">—</span>
                  )}
                </span>
                <span className={`text-right font-sheet ${numberClass(posting)}`}>
                  {formatMoney(posting.totalValueNoVat || 0)}
                </span>
              </>
            )}
            <span className={`text-right font-sheet font-semibold ${numberClass(posting)}`}>
              {formatMoney(posting.totalValueWithVat || 0)}
            </span>
            <span className="flex min-w-0">{statusPill(posting)}</span>
            <RowActionsMenu actions={actionsOf(posting)} onPrint={() => onPrint(posting)} dense />
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_TOTAL_LABEL_CLASS} ${full ? "col-span-7" : "col-span-4"}`}>
              {t(totalExcludesAnnulled ? "inventoryPage.periodTotalActive" : "inventoryPage.periodTotal")}
            </span>
            {full && (
              <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totalNet)}</span>
            )}
            <span className="text-right font-sheet font-bold text-brand-deep-ink">
              {formatMoney(totalGross)}
            </span>
            <span />
            <span />
          </div>
        )}
      </div>
    </div>
  );
};

export default PostedReportsTable;
